import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import MiniSelect from "@/components/MiniSelect";

// 년/월 드롭다운 선택지 — 서비스 데이터 시작(2026년) 전후로 넉넉히.
const YEAR_START = 2025;

// 커스텀 달력 팝오버(2026-07-18) — 브라우저 기본 date input 은 OS마다 생김새가 제각각이고
// 대시보드 톤과 안 맞아서 교체. SectionRangePicker 가 쓰는 공용 부품이라 한 곳만 고치면
// 모든 섹션의 날짜 선택이 같이 바뀐다. 오늘=파란 글씨, 선택일=파란 채움, min/max 밖=비활성.
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

function pad2(n) {
  return String(n).padStart(2, "0");
}

// open/onOpenChange(선택): 부모가 열림 상태를 제어하는 controlled 모드 — SectionRangePicker
// 가 "시작일을 고르면 종료일 달력이 자동으로 이어서 열리는" 흐름(2026-07-18 피드백)을
// 만들 때 쓴다. 안 넘기면 기존처럼 스스로 여닫는다.
export default function MiniDatePicker({ value, min, max, onChange, open: openProp, onOpenChange }) {
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = controlled ? openProp : openState;
  // view: 달력이 보여주는 달("YYYY-MM") — 열릴 때마다 선택된 날짜의 달로 리셋.
  const [view, setView] = useState(() => (value || "").slice(0, 7));
  const ref = useRef(null);

  const setOpen = (v) => {
    if (!controlled) setOpenState(v);
    onOpenChange?.(v);
  };

  useEffect(() => {
    if (open) setView((value || new Date().toISOString().slice(0, 10)).slice(0, 7));
    // value 는 의존성에서 제외 — 열려 있는 동안 날짜를 골라도 보던 달을 유지.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function toggle() {
    setOpen(!open);
  }

  function shiftMonth(delta) {
    const [y, m] = view.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setView(`${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`);
  }

  const [vy, vm] = view.split("-").map(Number);
  const firstWeekday = new Date(Date.UTC(vy, vm - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(vy, vm, 0)).getUTCDate();
  const today = new Date().toISOString().slice(0, 10);

  const label = value ? `${value.slice(0, 4)}. ${value.slice(5, 7)}. ${value.slice(8, 10)}.` : "날짜 선택";

  return (
    <span className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium tabular-nums ${
          open ? "bg-brand-50 text-brand-700" : "bg-ink-50 text-ink-700 hover:bg-ink-100"
        }`}
      >
        <CalendarDays className="h-3.5 w-3.5 text-ink-400" />
        {label}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 w-64 max-w-[calc(100vw-2rem)] rounded-xl border border-ink-100 bg-white p-3 shadow-[0_8px_24px_rgba(15,23,42,0.14)]">
          {/* 년/월은 화살표뿐 아니라 드롭다운으로도 점프(2026-07-18 피드백 — 몇 달씩
              화살표만 누르게 하지 않기). */}
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => shiftMonth(-1)} aria-label="이전 달"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="flex items-center gap-0.5">
              <MiniSelect
                plain
                value={String(vy)}
                onChange={(v) => setView(`${v}-${pad2(vm)}`)}
                options={Array.from(
                  { length: new Date().getFullYear() - YEAR_START + 1 },
                  (_, i) => ({ value: String(YEAR_START + i), label: `${YEAR_START + i}년` })
                )}
              />
              <MiniSelect
                plain
                value={pad2(vm)}
                onChange={(v) => setView(`${vy}-${v}`)}
                options={Array.from({ length: 12 }, (_, i) => ({ value: pad2(i + 1), label: `${i + 1}월` }))}
              />
            </span>
            <button type="button" onClick={() => shiftMonth(1)} aria-label="다음 달"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 text-center">
            {WEEKDAYS.map((w, i) => (
              <span key={w} className={`py-1 text-[11px] font-medium ${i === 0 ? "text-red-400" : "text-ink-400"}`}>{w}</span>
            ))}
            {Array.from({ length: firstWeekday }, (_, i) => <span key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const iso = `${vy}-${pad2(vm)}-${pad2(day)}`;
              const disabled = (min && iso < min) || (max && iso > max);
              const selected = iso === value;
              const isToday = iso === today;
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={disabled}
                  onClick={() => { onChange(iso); setOpen(false); }}
                  className={`mx-auto flex h-8 w-8 items-center justify-center rounded-lg text-xs tabular-nums transition-colors ${
                    selected
                      ? "bg-brand-600 font-semibold text-white"
                      : disabled
                        ? "cursor-not-allowed text-ink-200"
                        : isToday
                          ? "font-semibold text-brand-600 hover:bg-brand-50"
                          : "text-ink-700 hover:bg-ink-100"
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {/* "오늘" 바로가기(2026-07-18 피드백) — 몇 달 뒤 화면에서 화살표를 반복해서 누르지
              않고 한 번에 복귀. 범위(min/max) 밖이면 선택 대신 오늘이 있는 달로 이동만. */}
          <div className="mt-1.5 flex justify-end border-t border-ink-50 pt-1.5">
            <button
              type="button"
              onClick={() => {
                const disabled = (min && today < min) || (max && today > max);
                if (disabled) {
                  setView(today.slice(0, 7));
                } else {
                  onChange(today);
                  setOpen(false);
                }
              }}
              className="rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50"
            >
              오늘
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
